import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper, Stack, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import { aggiungiImpiantoGreen, elencaImpiantiGreen, impiantiDaAggiungere, type ImpiantoDaAggiungere } from './green.api'
import { ChipStato } from './Monitoraggio'

// I gestori di cui la spec prevede il prelievo (Documenti analisi §4.1); «Altro» resta manuale.
const GESTORI = ['E-Distribuzione', 'Unareti', 'Duereti', 'Altro']

export function Configurazione() {
  const client = useQueryClient()
  const { data: daAggiungere = [], error: e1 } = useQuery({ queryKey: ['green-da-aggiungere'], queryFn: impiantiDaAggiungere })
  const { data: green = [], error: e2 } = useQuery({ queryKey: ['impianti-green'], queryFn: elencaImpiantiGreen })
  const [scelto, setScelto] = useState<ImpiantoDaAggiungere | null>(null)
  const daCompletare = green.filter((g) => g.statoDistributore !== 'attivo' || g.statoGse !== 'attivo')

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="h5" component="h1">Configurazione</Typography>
        <Typography color="text.secondary">
          Aggiungi gli impianti al Controllo consumi e completa i collegamenti ai portali dei gestori.
        </Typography>
      </Box>
      {(e1 || e2) && <Alert severity="error">{(e1 ?? e2)!.message}</Alert>}

      <Paper variant="outlined">
        <Typography variant="h6" component="h2" sx={{ px: 2.5, pt: 2 }}>Impianti da aggiungere</Typography>
        <TableContainer>
          <Table size="small" aria-label="Impianti da aggiungere">
            <TableHead>
              <TableRow>
                <TableCell>Ragione sociale</TableCell>
                <TableCell>Codice impianto</TableCell>
                <TableCell>Comune</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {daAggiungere.map((i) => (
                <TableRow key={i.impiantoId}>
                  <TableCell>{i.ragioneSociale}</TableCell>
                  <TableCell>{i.codiceImpianto}</TableCell>
                  <TableCell>{i.comune}</TableCell>
                  <TableCell align="right">
                    <Button size="small" variant="outlined" onClick={() => setScelto(i)}>Aggiungi</Button>
                  </TableCell>
                </TableRow>
              ))}
              {daAggiungere.length === 0 && (
                <TableRow><TableCell colSpan={4}><Typography color="text.secondary" sx={{ py: 2 }}>Tutti gli impianti sono gia nel Controllo consumi.</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper variant="outlined">
        <Typography variant="h6" component="h2" sx={{ px: 2.5, pt: 2 }}>Collegamenti da completare</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ px: 2.5 }}>
          Senza credenziali dei portali i dati non si scaricano: l'impianto resta fra i non configurati.
        </Typography>
        <TableContainer>
          <Table size="small" aria-label="Collegamenti da completare">
            <TableHead>
              <TableRow>
                <TableCell>Ragione sociale</TableCell>
                <TableCell>Codice impianto</TableCell>
                <TableCell>Gestore di rete</TableCell>
                <TableCell>Distributore</TableCell>
                <TableCell>GSE</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {daCompletare.map((g) => (
                <TableRow key={g.id}>
                  <TableCell>{g.ragioneSociale}</TableCell>
                  <TableCell>{g.codiceImpianto}</TableCell>
                  <TableCell>{g.gestoreRete ?? '—'}</TableCell>
                  <TableCell><ChipStato stato={g.statoDistributore} /></TableCell>
                  <TableCell><ChipStato stato={g.statoGse} /></TableCell>
                </TableRow>
              ))}
              {daCompletare.length === 0 && (
                <TableRow><TableCell colSpan={5}><Typography color="text.secondary" sx={{ py: 2 }}>Nessun collegamento da completare.</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {scelto && (
        <DialogoAggiungi
          impianto={scelto}
          onChiudi={() => setScelto(null)}
          onAggiunto={() => {
            setScelto(null)
            void client.invalidateQueries({ queryKey: ['green-da-aggiungere'] })
            void client.invalidateQueries({ queryKey: ['impianti-green'] })
          }}
        />
      )}
    </Stack>
  )
}

function DialogoAggiungi({ impianto, onChiudi, onAggiunto }: {
  impianto: ImpiantoDaAggiungere; onChiudi: () => void; onAggiunto: () => void
}) {
  const [kwp, setKwp] = useState('')
  const [gestore, setGestore] = useState(GESTORI[0]!)
  const kwpValido = kwp === '' || (/^\d+([.,]\d+)?$/.test(kwp) && Number(kwp.replace(',', '.')) > 0)
  const aggiungi = useMutation({
    mutationFn: () => aggiungiImpiantoGreen(impianto.impiantoId, kwp.replace(',', '.'), gestore),
    onSuccess: onAggiunto,
  })

  return (
    <Dialog open onClose={onChiudi} fullWidth maxWidth="xs">
      <DialogTitle>Aggiungi {impianto.codiceImpianto}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">{impianto.ragioneSociale}</Typography>
          <TextField label="Potenza (kWp)" value={kwp} onChange={(e) => setKwp(e.target.value)}
            error={!kwpValido} helperText={kwpValido ? 'Facoltativa' : 'Un numero maggiore di zero'} />
          <TextField select label="Gestore di rete" value={gestore} onChange={(e) => setGestore(e.target.value)}>
            {GESTORI.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
          </TextField>
          {aggiungi.error && <Alert severity="error">{aggiungi.error.message}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onChiudi}>Annulla</Button>
        <Button variant="contained" disabled={!kwpValido || aggiungi.isPending} onClick={() => aggiungi.mutate()}>Aggiungi</Button>
      </DialogActions>
    </Dialog>
  )
}
